import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import moment from "moment";
import { isEmpty, orderBy } from "lodash";
import { Row } from "react-bootstrap";

import * as contractActions from "../../../actions/contractActions";
import * as exportActions from "../../../actions/exportActions";
import * as codeActions from "../../../actions/codeActions";

import Loading from "../../common/Loading";

import { formatDate } from "../../../helpers/date";

import TabsContainer from "./Tabs";
import Filters from "./Filters";

const MONTHS = [
  { label: "January", value: 1 },
  { label: "February", value: 2 },
  { label: "March", value: 3 },
  { label: "April", value: 4 },
  { label: "May", value: 5 },
  { label: "June", value: 6 },
  { label: "July", value: 7 },
  { label: "August", value: 8 },
  { label: "September", value: 9 },
  { label: "October", value: 10 },
  { label: "November", value: 11 },
  { label: "December", value: 12 }
];

const YEARS = ((back) => {
  const year = new Date().getFullYear();
  return Array.from({ length: back }, (v, i) => year - back + i + 1).map(
    (v) => ({
      label: v,
      value: v
    })
  );
})(10);

const getFilterValues = () => {
  const month = moment().subtract(1, "month").startOf("month").format("M");

  const year = formatDate(new Date(), "yyyy");

  return {
    startMonth: { label: MONTHS[month - 1].label, value: month },
    startYear: { label: year, value: year },
    endMonth: { label: MONTHS[month - 1].label, value: month },
    endYear: { label: year, value: year }
  };
};

const INITIAL_FILTERS_STATE = {
  startMonth: getFilterValues().startMonth,
  startYear: getFilterValues().startYear,
  endMonth: getFilterValues().endMonth,
  endYear: getFilterValues().endYear,
  contract: {}
};

const ITManagedServices = ({
  isMobile,
  actions,
  itmsContracts,
  projects,
  supportTickets,
  calls,
  loading,
  exporting,
  exported,
  codes,
  contracts
}) => {
  const [filters, setFilters] = useState(INITIAL_FILTERS_STATE);
  const [modifiedContracts, setModifiedContracts] = useState([]);
  const [filterError, setFilterError] = useState(null);

  const handleChangeFilters = (field, input) => {
    setFilters((prev) => {
      const newState = {
        ...prev,
        [field]: input
      };
      if (field !== "contract") {
        const startYear = parseInt(newState.startYear.value, 10);
        const startMonth = parseInt(newState.startMonth.value, 10);
        const endYear = parseInt(newState.endYear.value, 10);
        const endMonth = parseInt(newState.endMonth.value, 10);

        const monthsDifference =
          (endYear - startYear) * 12 + (endMonth - startMonth);

        if (monthsDifference > 12) {
          setFilterError({
            field,
            message: "Date range exceeds a max of 1 year (12 months)."
          });
        } else if (monthsDifference < 0) {
          setFilterError({
            field,
            message: "Start date cannot be after end date."
          });
        } else {
          setFilterError(null);
        }
      }
      return newState;
    });
  };

  const resetFilters = () => {
    setFilters(INITIAL_FILTERS_STATE);
    setFilterError(null);
    actions.clearITMSContracts();
  };

  useEffect(() => {
    if (isEmpty(codes)) {
      actions.loadCodes();
    }
  }, [actions, codes]);

  useEffect(() => {
    const contractType = codes.find(
      (code) =>
        code.entity === "Contract" &&
        code.field === "type" &&
        code.label === "MANAGED SERVICES"
    );
    const contractStatus = codes.find(
      (code) =>
        code.entity === "Contract" &&
        code.field === "status" &&
        code.label === "Active"
    );
    if (contractType && contractStatus) {
      actions.loadContracts({
        filtered: {
          contract_type: [contractType.id],
          contract_status: [contractStatus.id]
        }
      });
    }
  }, [actions, codes]);

  useEffect(() => {
    setModifiedContracts(
      orderBy(contracts, ["customer.name", "name"])
        .filter((contract) => contract.tier?.normal_sla > 0)
        .map((contract) => ({
          label: `${contract.customer?.name} - ${contract.name}`,
          value: contract._id
        }))
    );
  }, [contracts]);

  useEffect(
    () => () => {
      actions.clearITMSContracts();
      actions.clearContracts();
    },
    [actions]
  );

  useEffect(() => {
    if (exported) {
      const url = window.URL.createObjectURL(
        new Blob([exported], { type: "application/pdf" })
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = "IT-Managed-Services.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      actions.clearExported();
    }
  }, [exported, actions]);

  const handleFilterClick = () => {
    if (!isEmpty(filters.contract)) {
      actions.loadITMSContracts({
        filtered: {
          contract: filters.contract.value,
          period: [
            `${filters.startMonth.value},${filters.startYear.value}`,
            `${filters.endMonth.value},${filters.endYear.value}`
          ]
        }
      });
    }
  };

  const handleExportClick = () => {
    const payload = {
      data: {
        contract: filters.contract.value,
        period: [
          `${filters.startMonth.value},${filters.startYear.value}`,
          `${filters.endMonth.value},${filters.endYear.value}`
        ]
      },
      type: "IT_MANAGED_SERVICES",
      format: "pdf"
    };
    actions.performExport(payload);
  };

  return (
    <React.Fragment>
      <Filters
        multiple
        filterError={filterError}
        filters={{ ...filters, months: MONTHS, years: YEARS }}
        onChangeFilters={handleChangeFilters}
        onFilter={handleFilterClick}
        onReset={resetFilters}
        contracts={modifiedContracts}
        exporting={exporting}
        loading={loading}
        onPDFExport={handleExportClick}
        exportDisabled={exporting || loading || !itmsContracts.length}
      />
      {loading && (
        <Row style={{ display: "flex", justifyContent: "center" }}>
          <Loading />
        </Row>
      )}
      {!loading && (
        <TabsContainer
          contracts={itmsContracts}
          endMonth={filters.endMonth.value}
          endYear={filters.endYear.value}
          projects={projects}
          tickets={supportTickets}
          calls={calls}
          isMobile={isMobile}
          startMonth={filters.startMonth.value}
          startYear={filters.startYear.value}
        />
      )}
    </React.Fragment>
  );
};

ITManagedServices.propTypes = {
  actions: PropTypes.object,
  isMobile: PropTypes.bool,
  itmsContracts: PropTypes.array,
  projects: PropTypes.array,
  supportTickets: PropTypes.array,
  calls: PropTypes.array,
  loading: PropTypes.bool,
  exporting: PropTypes.bool,
  exported: PropTypes.string,
  codes: PropTypes.array,
  contracts: PropTypes.array
};

ITManagedServices.defaultProps = {
  itmsContracts: [],
  projects: [],
  supportTickets: [],
  calls: [],
  loading: false,
  exporting: false,
  exported: null,
  codes: [],
  contracts: []
};

function mapStatesToProps(state) {
  return {
    supportTickets: state.reducers.supportTickets,
    calls: state.reducers.calls,
    projects: state.reducers.projects,
    itmsContracts: state.reducers.itmsContracts,
    loading: state.reducers.loading,
    exporting: state.reducers.exporting,
    exported: state.reducers.exported,
    codes: state.reducers.codes,
    contracts: state.reducers.contracts
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        ...contractActions,
        ...exportActions,
        ...codeActions
      },
      dispatch
    )
  };
}

export default connect(mapStatesToProps, mapDispatchToProps)(ITManagedServices);
